import { FormEvent, MouseEvent, ReactNode, useEffect, useState } from 'react';
import { InView } from 'react-intersection-observer';
import { useSearchParams } from 'react-router-dom';

import {
  Box,
  Button,
  CircularProgress,
  IconButton,
  Tooltip,
  Typography,
} from '@material-ui/core';
import { Breakpoint } from '@material-ui/core/styles/createBreakpoints';
import { Alert } from '@material-ui/lab';

import {
  RiChatAi4Fill as Assistant,
  RiCloseLine as Close,
} from '@remixicon/react';
import capitalize from 'lodash/capitalize';
import isNil from 'lodash/isNil';

import { useAnalytics } from '@backstage/core-plugin-api';

import { NarrowBox, useFeatureFlag } from '@spotify/confidence-core-react';

import {
  StepProgressBar,
  TitleWrapper,
  WizardContent,
  WizardFooter,
  WizardHeader,
  WizardLoadingOverlay,
  WizardStepWrapper,
  WizardWrapper,
} from './WizardComponents';

export type WizardStep = {
  name: string;
  description?: ReactNode;
  content: ReactNode;
  optional?: boolean;
  disableNext?: boolean;
  forceReview?: boolean;
};

type WizardStepperProps = {
  onClose?: () => void;
  onSubmit?: () => void;
  steps?: WizardStep[];
  submitLabel?: string;
  cancelLabel?: string;
  backLabel?: string;
  nextLabel?: string;
  disableNext?: boolean;
  error?: Error;
  loading?: boolean;
  activeStep?: number;
  onStepChange?: (newStep: number) => void;
  formName?: string;
  width?: Breakpoint;
  subject?: string;
  onGenerateStep?: () => Promise<void>;
  disableGenerate?: boolean;
  generateError?: boolean;
  generateStepIndex?: number;
  loadingContent?: ReactNode;
};

export const WizardStepper = ({
  steps = [],
  onClose,
  onSubmit,
  submitLabel = 'Submit',
  backLabel = 'Back',
  nextLabel = 'Next',
  cancelLabel = 'Cancel',
  disableNext = false,
  error,
  loading,
  activeStep: controlledActiveStep,
  onStepChange,
  formName,
  width = 'sm',
  subject,
  onGenerateStep,
  disableGenerate,
  generateError = false,
  generateStepIndex = 0,
  loadingContent,
}: WizardStepperProps) => {
  const [searchParams, setUrlSearchParams] = useSearchParams();
  const [internalActiveStepIndex, setInternalActiveStepIndex] = useState(0);
  const [isLoadingFirstStep, setIsLoadingFirstStep] = useState(false);
  const [isCompletedGenerated, setIsCompletedGenerated] = useState(false);
  const [shouldNavigateAfterGenerate, setShouldNavigateAfterGenerate] =
    useState(true);
  const searchIndex = searchParams.get('step')
    ? Number(searchParams.get('step'))
    : undefined;
  const activeStepIndex = controlledActiveStep ?? internalActiveStepIndex;
  const activeStep = steps[activeStepIndex] || steps[0];
  const lastIndex = steps.length - 1;
  const isLastStep = activeStepIndex === lastIndex;
  const analytics = useAnalytics();

  const [hasSeenFullStep, setHasSeenFullStep] = useState(false);
  const progressPercentage = (activeStepIndex / lastIndex) * 100;

  const { enabled: generateOnNextEnabled } = useFeatureFlag<{
    enabled: boolean;
  }>('wizard-generate-on-next', {
    enabled: false,
  });

  const isGenerateStep = activeStepIndex === generateStepIndex;

  const handleNextStep = (e?: MouseEvent<HTMLButtonElement>) => {
    if (!isLastStep) {
      e?.preventDefault();

      if (
        generateOnNextEnabled &&
        isGenerateStep &&
        onGenerateStep &&
        !isLoadingFirstStep &&
        !isCompletedGenerated
      ) {
        setShouldNavigateAfterGenerate(false);
        setIsLoadingFirstStep(true);
        onGenerateStep();
        return;
      }

      setInternalActiveStepIndex(current => {
        const nextStep = Math.min(
          (controlledActiveStep ?? current) + 1,
          lastIndex,
        );
        onStepChange?.(nextStep);

        searchParams.set('step', String(nextStep));
        setUrlSearchParams(searchParams);
        return nextStep;
      });
    }
  };

  const handleGoBack = () => {
    setIsCompletedGenerated(false);
    setInternalActiveStepIndex(current => {
      const nextStep = Math.max((controlledActiveStep ?? current) - 1, 0);
      onStepChange?.(nextStep);

      searchParams.set('step', String(nextStep));
      setUrlSearchParams(searchParams);
      return nextStep;
    });
  };

  const handleClose = () => {
    searchParams.delete('step');
    setUrlSearchParams(searchParams);
    onClose?.();
  };

  const handleSubmit = (e: FormEvent) => {
    e.preventDefault();
    if (isLastStep) {
      onSubmit?.();
    } else {
      handleNextStep();
    }
  };

  const handleGenerateStep = async () => {
    setShouldNavigateAfterGenerate(true);
    setIsLoadingFirstStep(true);
    await onGenerateStep?.();
  };

  useEffect(() => {
    if (
      activeStepIndex === generateStepIndex &&
      isLoadingFirstStep &&
      !loading
    ) {
      setIsLoadingFirstStep(false);
      if (!generateError) {
        setIsCompletedGenerated(true);

        if (shouldNavigateAfterGenerate) {
          setInternalActiveStepIndex(() => {
            const nextStep = lastIndex;
            onStepChange?.(nextStep);

            searchParams.set('step', String(nextStep));
            setUrlSearchParams(searchParams);
            return nextStep;
          });
        } else {
          // Navigate to the next step after background generation
          setInternalActiveStepIndex(() => {
            const nextStep = activeStepIndex + 1;
            onStepChange?.(nextStep);

            searchParams.set('step', String(nextStep));
            setUrlSearchParams(searchParams);
            return nextStep;
          });
        }
      }
    }
  }, [
    loading,
    activeStepIndex,
    isLoadingFirstStep,
    isCompletedGenerated,
    generateError,
    generateStepIndex,
    lastIndex,
    shouldNavigateAfterGenerate,
    onStepChange,
    searchParams,
    setUrlSearchParams,
  ]);

  useEffect(() => {
    if (!isNil(controlledActiveStep) && controlledActiveStep !== searchIndex) {
      searchParams.set('step', String(controlledActiveStep));
      setUrlSearchParams(searchParams);
    }
    // One-way controlled-prop -> URL sync; depending on `searchParams` would
    // re-add ?step after the post-submit navigation deletes it.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [controlledActiveStep]);

  useEffect(() => {
    if (!isNil(searchIndex)) {
      const normalizedSearchIndex = Math.max(
        Math.min(searchIndex, lastIndex),
        0,
      );
      if (normalizedSearchIndex !== activeStepIndex) {
        onStepChange?.(normalizedSearchIndex);
        setInternalActiveStepIndex(normalizedSearchIndex);
      }
    }
    // One-way URL -> state sync; depending on `activeStepIndex` could snap
    // in-flight navigation back if the URL update lands a render late.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchIndex]);

  useEffect(() => {
    if (subject) {
      analytics.captureEvent('impression', subject, {
        value: activeStepIndex,
      });
    }
  }, [subject, activeStepIndex, analytics]);

  const isGeneratingOnNext =
    isGenerateStep && isLoadingFirstStep && !shouldNavigateAfterGenerate;

  let nextIsDisabled =
    loading || (isLoadingFirstStep && shouldNavigateAfterGenerate);
  let disabledTooltip = '';

  if (disableNext || activeStep.disableNext) {
    nextIsDisabled = true;
    disabledTooltip =
      'It is not possible to proceed yet. Did you enter all the requested information?';
  } else if (
    !activeStep.optional &&
    !hasSeenFullStep &&
    activeStep?.forceReview
  ) {
    nextIsDisabled = true;
    disabledTooltip =
      'Please review this step before proceeding by scrolling down.';
  } else if (isGeneratingOnNext) {
    nextIsDisabled = true;
  }

  let generateTooltip = 'Let Confidence generate a suggestion for you.';
  if (disableGenerate) {
    generateTooltip =
      'Provide more context and let Confidence generate a suggestion for you.';
  } else if (nextIsDisabled) {
    generateTooltip = disabledTooltip;
  }

  const { copy } = useFeatureFlag<{ copy: string }>('generate-button', {
    copy: 'Generate',
  });

  return (
    <WizardWrapper>
      <WizardHeader>
        {onClose && (
          <IconButton data-testid="close-wizard" onClick={handleClose}>
            <Close />
          </IconButton>
        )}
      </WizardHeader>

      <form onSubmit={handleSubmit} name={formName}>
        <NarrowBox width={width}>
          <WizardContent>
            {steps.length > 1 && (
              <>
                <StepProgressBar
                  variant="determinate"
                  value={progressPercentage}
                />
                <Typography variant="body2" color="textSecondary">
                  Step {activeStepIndex + 1} of {lastIndex + 1}{' '}
                  {activeStep.optional ? (
                    <Typography
                      variant="inherit"
                      color="textSecondary"
                      component="span"
                    >
                      (optional)
                    </Typography>
                  ) : null}
                </Typography>
              </>
            )}

            <WizardStepWrapper>
              <TitleWrapper>
                <Typography variant="h4">{activeStep.name}</Typography>
                <Typography variant="body1" color="textSecondary">
                  {activeStep.description}
                </Typography>
              </TitleWrapper>
              {error && <Alert severity="error">{error.message}</Alert>}
              {activeStep.content}
              <InView onChange={() => setHasSeenFullStep(true)} />
            </WizardStepWrapper>

            <WizardFooter>
              {onClose && activeStepIndex === 0 && (
                <Button onClick={handleClose} disabled={loading}>
                  {cancelLabel}
                </Button>
              )}
              {activeStepIndex > 0 && (
                <Button onClick={handleGoBack} disabled={loading}>
                  {backLabel}
                </Button>
              )}

              {isGenerateStep && onGenerateStep && (
                <Tooltip title={generateTooltip} arrow placement="right-start">
                  <div>
                    <Button
                      disabled={nextIsDisabled || disableGenerate}
                      variant="contained"
                      color="secondary"
                      onClick={handleGenerateStep}
                      type="button"
                      startIcon={<Assistant />}
                    >
                      {capitalize(copy)}
                    </Button>
                  </div>
                </Tooltip>
              )}
              <Tooltip title={disabledTooltip} arrow placement="right-start">
                <div>
                  <Button
                    disabled={nextIsDisabled}
                    variant="contained"
                    color="primary"
                    onClick={handleNextStep}
                    type={isLastStep ? 'submit' : 'button'}
                    startIcon={
                      isGeneratingOnNext ? (
                        <CircularProgress size={20} />
                      ) : undefined
                    }
                  >
                    {isLastStep ? submitLabel : nextLabel}
                  </Button>
                </div>
              </Tooltip>
            </WizardFooter>
          </WizardContent>
        </NarrowBox>
      </form>
      {((loading && !isGeneratingOnNext) ||
        (isLoadingFirstStep && shouldNavigateAfterGenerate)) && (
        <WizardLoadingOverlay>
          {isLoadingFirstStep ? (
            <Box display="flex" flexDirection="column" alignItems="center">
              <Box display="block" mb={2}>
                <Typography variant="h6">Generating...</Typography>
              </Box>
              <Box display="block">
                <CircularProgress />
              </Box>
            </Box>
          ) : (
            (loadingContent ?? <CircularProgress />)
          )}
        </WizardLoadingOverlay>
      )}
    </WizardWrapper>
  );
};
